import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import dateformat from 'utils/dateformat';
import Api from 'services/Api';
import Reload from '../../components/Reload/Reload';
import { Map, Filter, Timeline } from './components';
import './MapsTracking.css';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const parseCoordinate = value => {
  const coordinate = Number.parseFloat(value);
  return Number.isFinite(coordinate) ? Number(coordinate.toFixed(7)) : null;
};

const isValidCoordinate = (lat, lng) =>
  lat !== null &&
  lng !== null &&
  lat !== 0 &&
  lng !== 0 &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

const toRadians = degrees => (degrees * Math.PI) / 180;

/** Distância em metros entre dois pontos (Haversine). */
const haversineMeters = (a, b) => {
  const earthRadius = 6371000;
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(h));
};

/** Soma das distâncias entre pontos consecutivos do trajeto. */
const getPathDistanceMeters = (pathPoints = []) => {
  if (pathPoints.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < pathPoints.length; i += 1) {
    total += haversineMeters(pathPoints[i - 1], pathPoints[i]);
  }
  return total;
};

const formatDistance = meters => {
  if (!Number.isFinite(meters) || meters <= 0) return null;
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2
  })} km`;
};

const formatMeasuresToPoints = (measures = []) =>
  measures
    .map((measure, index) => {
      const lat = parseCoordinate(measure?.lat ?? measure?.latitude);
      const lng = parseCoordinate(measure?.lng ?? measure?.longitude);

      if (!isValidCoordinate(lat, lng)) {
        return null;
      }

      return {
        type: 'Feature',
        properties: {
          cluster: false,
          id: measure?._id || `${measure?.serialnumber}-${index}`,
          order: index + 1,
          data: {
            serial: measure?.serialnumber || '',
            date: measure?.date ? dateformat(measure.date) : '--',
            grain: measure?.grain || '--',
            humidity: measure?.humidity || '--',
            temperature: measure?.temperature || '--',
            city: measure?.city || '--',
            state: measure?.state || '--',
            email: measure?.email || measure?.userEmail || '',
            lat,
            lng,
            order: index + 1,
            isStart: index === 0,
            isEnd: index === measures.length - 1
          }
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        }
      };
    })
    .filter(Boolean);

const MapsTracking = () => {
  const user = JSON.parse(localStorage.getItem('userData') || 'null') || {};

  useEffect(() => {
    const canAccess =
      user?.permission?.maps?.read === true ||
      user?.permission?.mapsunits?.read === true;
    if (!canAccess) {
      window.location.replace('/home');
    }
  }, [user]);

  const [serialInput, setSerialInput] = useState('');
  const [activeSerial, setActiveSerial] = useState('');
  const [showSerialModal, setShowSerialModal] = useState(true);
  const [points, setPoints] = useState([]);
  const [path, setPath] = useState([]);
  const [distanceMeters, setDistanceMeters] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState(
    'Informe o número de série para ver o trajeto das medidas.'
  );
  const [zoom] = useState(5);
  const [center] = useState({ lat: -14.8227361, lng: -54.1549677 });
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('theme') === 'dark'
  );

  useEffect(() => {
    const handleThemeChange = () => {
      setDarkMode(document.body.classList.contains('dark-theme'));
    };

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const fetchTrack = useCallback(async serialValue => {
    const serial = String(serialValue || '').trim().toUpperCase();
    if (!serial) {
      setError('Informe um número de série válido.');
      return;
    }

    setLoading(true);
    setError('');
    setInfoMessage('');
    setPoints([]);
    setPath([]);
    setDistanceMeters(0);
    setSelectedIndex(null);
    setActiveSerial(serial);

    try {
      const encoded = encodeURIComponent(serial);
      const { data } = await Api.get(`/api/v1/measurementsbench/recent/${encoded}`);
      const list = Array.isArray(data) ? [...data].reverse() : [];

      if (!list.length) {
        setInfoMessage(`Nenhuma medida encontrada para o equipamento ${serial}.`);
        return;
      }

      const mappedPoints = formatMeasuresToPoints(list);

      if (!mappedPoints.length) {
        setInfoMessage(
          `O equipamento ${serial} possui medidas, mas nenhuma com coordenadas válidas.`
        );
        return;
      }

      // Reindexa ordem só entre pontos com GPS
      const orderedPoints = mappedPoints.map((point, index) => ({
        ...point,
        properties: {
          ...point.properties,
          order: index + 1,
          data: {
            ...point.properties.data,
            order: index + 1,
            isStart: index === 0,
            isEnd: index === mappedPoints.length - 1
          }
        }
      }));

      setPoints(orderedPoints);
      const nextPath = orderedPoints.map(point => ({
        lat: point.properties.data.lat,
        lng: point.properties.data.lng
      }));
      setPath(nextPath);
      setDistanceMeters(getPathDistanceMeters(nextPath));
      setSelectedIndex(orderedPoints.length ? 0 : null);
      setInfoMessage(
        `${orderedPoints.length} ponto(s) de medida com localização para ${serial}.`
      );
    } catch (err) {
      console.error('Erro ao buscar rastreio de medidas:', err);
      setError('Não foi possível buscar as medidas deste equipamento.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmitSerial = event => {
    event.preventDefault();
    setShowSerialModal(false);
    fetchTrack(serialInput);
  };

  const handleOpenSerialModal = () => {
    setSerialInput(activeSerial || '');
    setShowSerialModal(true);
  };

  return (
    <div
      className="map-container"
      style={{
        width: '100%',
        height: '100vh',
        backgroundColor: 'var(--map-background)',
        position: 'relative'
      }}
    >
      {loading && <Reload />}

      <Filter
        count={points.length}
        serial={activeSerial}
        distanceLabel={formatDistance(distanceMeters)}
        darkMode={darkMode}
        message={infoMessage}
        onSearch={handleOpenSerialModal}
      />

      {error && (
        <Alert
          variant="danger"
          className="maps-tracking-alert"
          onClose={() => setError('')}
          dismissible
        >
          {error}
        </Alert>
      )}

      <Map
        center={center}
        containerElement={<div style={{ height: '100%' }} />}
        googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=${GOOGLE_API_KEY}&language=pt-br`}
        loadingElement={<div style={{ height: '100%' }} />}
        mapElement={<div style={{ height: '100%' }} />}
        points={points}
        path={path}
        zoom={zoom}
        darkMode={darkMode}
        selectedIndex={selectedIndex}
        onSelectIndex={setSelectedIndex}
      />

      {points.length > 0 && (
        <Timeline
          points={points}
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
          darkMode={darkMode}
          distanceLabel={formatDistance(distanceMeters)}
        />
      )}

      <Modal
        show={showSerialModal}
        onHide={() => setShowSerialModal(false)}
        centered
        backdrop="static"
        contentClassName={darkMode ? 'bg-dark text-light border-secondary' : undefined}
      >
        <Form onSubmit={handleSubmitSerial}>
          <Modal.Header
            closeButton={!loading}
            closeVariant={darkMode ? 'white' : undefined}
            className={darkMode ? 'bg-dark text-light border-secondary' : undefined}
          >
            <Modal.Title>Rastreio de medidas</Modal.Title>
          </Modal.Header>
          <Modal.Body className={darkMode ? 'bg-dark text-light' : undefined}>
            <Form.Group controlId="trackingSerial">
              <Form.Label>Número de série do equipamento</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex.: CNMT-01359"
                value={serialInput}
                onChange={e => setSerialInput(e.target.value.toUpperCase())}
                autoFocus
                required
                className={darkMode ? 'bg-dark text-light border-secondary' : undefined}
              />
              <Form.Text className={darkMode ? 'text-secondary' : undefined} muted={!darkMode}>
                Se existirem medidas com localização, o trajeto será exibido no mapa.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className={darkMode ? 'bg-dark text-light border-secondary' : undefined}>
            <Button
              variant={darkMode ? 'outline-light' : 'secondary'}
              onClick={() => setShowSerialModal(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              style={{
                backgroundColor: 'var(--brand-dark-green, #1c4220)',
                borderColor: 'var(--brand-dark-green, #1c4220)'
              }}
              disabled={loading || !serialInput.trim()}
            >
              Buscar trajeto
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default MapsTracking;
